import { EntityTagType, TagTypeEnum, UserType, UserViewFieldSchema } from '@/types/schema';

import { GroupedActionItem } from '../../../types';
import { TaskGroupByEnum } from '../../TaskDashboard/types';
import { getTagGroupById, TAG_GROUP_ID_PREFIX } from '../../TaskGroupByFilter/utils';
import { GroupedByStageType, GroupedByStatusType } from '../types';

interface Params {
  groupBy?: string;
  sortedTaskViewConfig?: UserViewFieldSchema[] | null;
  groupedActionItem: GroupedActionItem;
  groupByKey?: string;
}

export const getGroupedByUser = (params: Params) => {
  const { groupBy, sortedTaskViewConfig, groupedActionItem, groupByKey } = params;
  const actionItems = groupedActionItem.actionItems;

  if (groupBy === TaskGroupByEnum.Assignee) {
    return actionItems?.[0]?.assignedToUsers?.find(
      (user) => user?.id === groupByKey,
    ) as UserType | null;
  }

  if (groupBy?.startsWith(TAG_GROUP_ID_PREFIX)) {
    const config = sortedTaskViewConfig?.find(
      (config) => groupBy === getTagGroupById(config.entityId!),
    );

    if (config) {
      const getTagValue = (actionItem: GroupedActionItem['actionItems'][0]) =>
        actionItem.tags
          ?.find((tag) => tag && tag.tagId === config.entityId)
          ?.users?.find((user) => user.id === groupedActionItem.groupByKey) as UserType | undefined;

      let user: UserType | undefined;
      actionItems.find((actionItem) => {
        user = getTagValue(actionItem);
        return user;
      });

      return user;
    }
  }

  return null;
};

export const getGroupedByStatus = (params: Params): GroupedByStatusType | null => {
  const { groupBy, groupedActionItem } = params;
  const actionItems = groupedActionItem.actionItems;

  if (groupBy === TaskGroupByEnum.Status) {
    if (!actionItems?.[0]) {
      return null;
    }
    return {
      status: actionItems[0].status,
      statusId: actionItems[0].statusId,
      category: actionItems[0].category,
      statusInfo: actionItems[0].statusInfo,
    };
  }

  return null;
};

export const getGroupedByStage = (params: Params): GroupedByStageType | null => {
  const { groupBy, groupedActionItem } = params;
  const actionItems = groupedActionItem.actionItems;

  if (groupBy === TaskGroupByEnum.Stage) {
    if (!actionItems?.[0]?.stages?.[0]) {
      return null;
    }
    return {
      stageId: actionItems[0].stages[0].stageId,
      name: actionItems[0].stages[0].name,
      iconSettings: actionItems[0].stages[0].iconSettings,
    };
  }

  return null;
};

export const getGroupedByTag = (params: Params): EntityTagType | null => {
  const { groupBy, groupedActionItem, sortedTaskViewConfig } = params;
  const firstActionItem = groupedActionItem.actionItems[0];
  const groupByKey = groupedActionItem.groupByKey;

  // Early validation
  if (!groupBy?.startsWith(TAG_GROUP_ID_PREFIX) || !firstActionItem) {
    return null;
  }

  const config = sortedTaskViewConfig?.find(
    (config) => groupBy === getTagGroupById(config.entityId!),
  );

  if (!config) {
    return null;
  }

  const tag = firstActionItem.tags?.find((tag) => tag?.tagId === config.entityId);

  if (!tag) {
    return null;
  }

  // Handle different tag types
  switch (tag.type) {
    case TagTypeEnum.User: {
      const selectedUser = tag.users?.find((user) => user.id === groupByKey);
      return {
        ...tag,
        users: selectedUser ? [selectedUser] : [],
        userIdValues: selectedUser?.id ? [selectedUser.id] : [],
      } as EntityTagType;
    }

    case TagTypeEnum.Date: {
      const hasMatchingDate =
        tag.dateValue?.startTime === groupByKey || tag.dateValue?.endTime === groupByKey;
      return {
        ...tag,
        dateValue: hasMatchingDate ? tag.dateValue : undefined,
      } as EntityTagType;
    }

    default: {
      // Handle option-based tags (Select, MultiSelect, etc.)
      const selectedOption = tag.options?.find(
        (option) => option.name === groupByKey || option.id === groupByKey,
      );
      return {
        ...tag,
        options: selectedOption ? [selectedOption] : [],
        optionIdValues: selectedOption?.id ? [selectedOption.id] : [],
      } as EntityTagType;
    }
  }
};

export type ExpandedRowKeys = (string | number)[];
